import React, { useEffect, useMemo } from 'react';
import { CreateTokenForm } from '../components/explore/CreateTokenSection';
import { trackWidgetLoad } from '../utils/supabase';
import styled from 'styled-components';

const WidgetContainer = styled.div<{ $theme: 'light' | 'dark'; $primaryColor: string }>`
  width: 100%;
  max-width: 560px;
  min-height: 720px;
  margin: 0 auto;
  padding: 16px;
  background: ${props => props.$theme === 'light' ? '#ffffff' : '#0b0f14'};
  color: ${props => props.$theme === 'light' ? '#111827' : '#e5e7eb'};
  border: 1px solid ${props => props.$theme === 'light' ? '#e5e7eb' : '#1f2937'};
  border-radius: 12px;
  box-sizing: border-box;

  @media (max-width: 600px) {
    max-width: 100%;
    min-height: auto;
    padding: 12px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    gap: 8px;
    margin-bottom: 10px;
  }

  img {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    flex-shrink: 0;

    @media (max-width: 600px) {
      width: 24px;
      height: 24px;
    }
  }

  div {
    font-weight: 600;
    font-size: 16px;

    @media (max-width: 600px) {
      font-size: 14px;
    }
  }
`;

const WidgetFooter = styled.div<{ $theme: 'light' | 'dark'; $primaryColor: string }>`
  margin-top: 14px;
  border-top: 1px solid ${props => props.$theme === 'light' ? '#e5e7eb' : '#1f2937'};
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;

  @media (max-width: 600px) {
    margin-top: 12px;
    padding-top: 8px;
    font-size: 11px;
    flex-direction: column;
    gap: 6px;
    text-align: center;
  }

  a {
    color: ${props => props.$primaryColor};
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const WidgetLaunchPage: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const theme = (params.get('theme') === 'light' ? 'light' : 'dark') as 'light' | 'dark';
  const primaryColor = params.get('primaryColor') || '#7c3aed';
  const heading = params.get('heading') || '';
  const logoUrl = params.get('logoUrl') || '';
  const allowedDexParam = params.get('allowedDex'); // 'protofire' | 'inkyswap' | 'both'
  const allowedDex = (allowedDexParam === 'protofire' || allowedDexParam === 'inkyswap') ? allowedDexParam : 'both';

  // Check if widget is embedded in iframe
  const isEmbedded = typeof window !== 'undefined' && window.parent !== window;

  // Enforced desktop dimensions for embedded widget
  const enforcedWidth = 560;   // px (non-negotiable target for desktop embeds)
  const enforcedMinHeight = 720; // px

  useEffect(() => {
    // Track widget load
    const queryParamsObj: Record<string, string> = {};
    params.forEach((value, key) => {
      queryParamsObj[key] = value;
    });
    
    trackWidgetLoad(
      document.referrer,
      isEmbedded ? 'iframe' : undefined,
      queryParamsObj
    ).catch(err => console.error('Failed to track widget load:', err));

    try {
      if (isEmbedded) {
        const size = { width: enforcedWidth, minHeight: enforcedMinHeight };
        window.parent.postMessage({ type: 'mavrk:ready', size }, '*');
      }
    } catch {}

    const onUnhandled = (e: PromiseRejectionEvent) => {
      try {
        if (isEmbedded) {
          window.parent.postMessage({ type: 'mavrk:error', message: String(e.reason?.message || e.reason) }, '*');
        }
      } catch {}
    };
    window.addEventListener('unhandledrejection', onUnhandled);
    return () => window.removeEventListener('unhandledrejection', onUnhandled);
  }, [params, isEmbedded]);

  return (
    <>
      <div style={{ padding: isEmbedded ? '0' : '24px', background: theme === 'light' ? '#f9fafb' : '#0b0f14', minHeight: '100vh' }}>
        <WidgetContainer $theme={theme} $primaryColor={primaryColor}>
          {(heading || logoUrl) && (
            <WidgetHeader>
              {logoUrl ? <img src={logoUrl} alt="Brand Logo" /> : null}
              {heading ? <div>{heading}</div> : null}
            </WidgetHeader>
          )}

          <div style={{ '--mv-primary': primaryColor } as React.CSSProperties}>
            <CreateTokenForm
              isVisible={true}
              asPage={true}
              allowedDex={allowedDex as any}
              compact
              onClose={() => {
                /* no-op in widget */
              }}
            />
          </div>

          <WidgetFooter $theme={theme} $primaryColor={primaryColor}>
            <span>Powered by Mavrk</span>
            <a href="https://mavrk.ink" target="_blank" rel="noreferrer">mavrk.ink</a>
          </WidgetFooter>
        </WidgetContainer>
      </div>
    </>
  );
};

export default WidgetLaunchPage;


